/**
 * ProtectedRoutes: wrapper for pages that require authentication.
 *
 * Redirects unauthenticated or unverified users to /login.
 * Redirects authenticated users without a MongoDB profile to /create-profile.
 * Redirects users who already have a profile away from /create-profile to
 * the chooser (which routes them to their tenant dashboard).
 *
 * Wave K: when a logged-in user lands on a tenant-scoped route AT THE PLATFORM
 * APEX (no ecosystem resolved), redirect to /choose-ecosystem rather than
 * letting the page render against null tenant context. Without this guard a
 * stale `/admin` bookmark on the apex renders the Dashboard against a
 * null ecosystem and the page is broken; the same goes for `/directory`,
 * `/opportunities`, etc. /profile, /profile/edit, /create-profile,
 * /choose-ecosystem, and /dashboard are all platform-level surfaces that DO render at the
 * apex, so they are not in the tenant-scoped set.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '@/shared/contexts/AuthContext';
import { useEcosystem } from '@/shared/contexts/EcosystemContext';
import { sanitizeRedirectPath } from '@/shared/utils/safeRedirect';

// Path prefixes for the protected routes that REQUIRE an active tenant
// context. A signed-in user hitting one of these at the platform apex is
// redirected to the chooser instead of letting the page render against null
// tenant context. Matched by prefix because most live under nested routes
// (`/admin/...`, `/organizations/:id/...`).
//
// NOT in the list: /profile, /profile/edit, /create-profile,
// /choose-ecosystem, /dashboard: these are platform-level user surfaces that render
// fine at the apex.
//
// Public-route pages (/directory, /opportunities, etc.) are not wrapped by
// ProtectedRoutes so this guard cannot enforce them; they handle the
// no-ecosystem case on their own.
const TENANT_SCOPED_PREFIXES = ['/assistant', '/admin', '/organizations'];

/**
 * Decide whether a given pathname is one of the tenant-scoped routes guarded
 * by ProtectedRoutes.
 *
 * @param {string} pathname
 * @returns {boolean}
 */
function isTenantScopedPath(pathname) {
    return TENANT_SCOPED_PREFIXES.some(
        (prefix) =>
            pathname === prefix ||
            pathname.startsWith(`${prefix}/`) ||
            pathname.startsWith(`${prefix}?`)
    );
}

const ProtectedRoutes = ({ children }) => {
    const { user, loading, hasProfile, profileLoading } = useAuth();
    const { isPlatform, loading: ecosystemLoading } = useEcosystem();
    const location = useLocation();

    if (loading || profileLoading || ecosystemLoading) {
        return <div>Loading...</div>;
    }

    if (!user) {
        // Capture the current path and pass it as a redirect parameter.
        // Validate via the shared safeRedirect helper to reject open-redirect
        // payloads (protocol-relative, scheme URLs, whitespace tricks).
        const candidate = location.pathname + location.search;
        const safeRedirect = sanitizeRedirectPath(candidate);
        const loginUrl = safeRedirect
            ? `/login?redirect=${encodeURIComponent(safeRedirect)}`
            : '/login';
        return <Navigate to={loginUrl} replace />;
    }

    // If user doesn't have email verified, redirect to login
    if (!user.emailVerified) return <Navigate to="/login" replace />;

    // If no MongoDB profile and not on create-profile page, redirect to create-profile
    if (hasProfile === false && location.pathname !== '/create-profile') {
        return <Navigate to="/create-profile" replace />;
    }

    // If has profile but on create-profile page, redirect to the chooser:
    // it routes 0/1/N memberships appropriately (Wave K).
    if (hasProfile === true && location.pathname === '/create-profile') {
        return <Navigate to="/choose-ecosystem" replace />;
    }

    // Wave K: tenant-scoped route at the platform apex with no ecosystem
    // resolved: hand off to the chooser instead of rendering a broken
    // tenant page. EcosystemAdminRoute applies its own stricter check for
    // /admin/*, but it does not catch the broader set (/organizations/onboard,
    // /assistant, etc.); this guard backstops them all.
    if (isPlatform && isTenantScopedPath(location.pathname)) {
        return <Navigate to="/choose-ecosystem" replace />;
    }

    return children;
};

ProtectedRoutes.propTypes = {
    children: PropTypes.node.isRequired,
};

export default ProtectedRoutes;
